import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { config } from '@keytrade/functions';
import { Icon } from '@keytrade/components-icon';

import { mediaQuery } from '@/utils/helpers';
import useTranslations from '@/hooks/useTranslations';

import MobileSubPanelFilters from '../../filters/MobileSubPanelFilters';
import SaveFilter from '../../filters/SaveFilter';

const { list: colors } = config.colors;
const { getFont } = config.fonts;

const MobileFilterBlock = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 12px;
  background-color: ${colors.BlueLight};
  padding: 12px 0 12px 20px;
  margin-bottom: 24px;
  ${mediaQuery.md(`
    display: none;
  `)}
`;

const MobileFilterText = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-family: ${({ theme }) => getFont(theme, 'semiBold')};
  color: ${colors.BlueDark};
`;
const MobileFilterAmount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  border-left: 1px solid ${colors.BlueCloudy};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
`;

const BackgroundLayer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.2);
  pointer-events: all;
  transition: opacity 0.2s cubic-bezier(0.23, 1, 0.32, 1);
`;

const Panel = styled.div`
  position: absolute;
  right: calc(100% - 52px);
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  width: calc(100% - 52px);
  height: 100%;
  overflow: auto;
  background-color: ${colors.White};
  z-index: 99;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.08),
    0px 12px 20px 0px rgba(0, 0, 0, 0.14);
  transform: translateX(100%);
  transition: transform 0.2s, box-shadow 0.2s cubic-bezier(0.23, 1, 0.32, 1);
`;

const CloseWrapper = styled.a`
  display: inline-flex;
  justify-content: center;
  position: absolute;
  left: 8px;
  margin-top: 18px;
  width: 36px;
  height: 36px;
  align-items: center;
  background-color: ${colors.BlueDark};
  border-radius: 50%;
`;

const PanelInformation = styled.div``;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 20px 24px 25px;
  border-bottom: 2px solid ${colors.BlueLight};
`;

const PanelHeaderTitle = styled.span`
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  color: ${colors.BlueDark};
`;
const ClearFilters = styled.span`
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  color: ${colors.GreyBlue};
`;

const MobileFilters = styled.ul`
  padding: 0;
  margin: 0;
`;

const Filter = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  list-style-type: none;
  padding: 20px;
  border-bottom: 1px solid ${colors.BlueLight};
  &:last-of-type {
    border-bottom: none;
  }
`;
const FilterTitle = styled.span`
  font-size: 16px;
  font-family: ${({ theme }) => getFont(theme, 'Regular')};
  line-height: 24px;
  color: ${colors.BlueDark};
`;

const FilterInformation = styled.div`
  display: flex;
  align-items: center;
`;

const AtciveFilters = styled.span`
  display: inline-block;
  margin-right: 15px;
  font-size: 16px;
  font-family: ${({ theme }) => getFont(theme, 'SemiBold')};
  line-height: 24px;
  color: ${colors.BlueSky};
`;

const TotalAmount = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  background-color: ${colors.Orange};
  border-radius: 50%;
  color: ${colors.White};
  width: 24px;
  height: 24px;
  font-size: 16px;
  line-height: 24px;
  font-family: ${({ theme }) => getFont(theme, 'Bold')};
`;

type FilterData = {
  content: Array<any>;
  handleFilter: any;
  contentSelected: Array<any>;
};

export type MobileFilterData = {
  teams: FilterData;
  levels: FilterData;
  contractTypes: FilterData;
  clearMobileFilters: any;
};

type Props = MobileFilterData;
const MobileJobFilters: React.FC<Props> = (props) => {
  const { clearMobileFilters } = props;
  const [MobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [activeSubPanel, setActiveSubPanel] = useState('');
  const [totalAmountOfFilters, setTotalAmountOfFilters] = useState(0);

  const OpenMobileFilters = () => {
    setMobileFilterOpen(true);
  };

  const closeFilterPanel = () => {
    setMobileFilterOpen(false);
  };

  const openSubFilters = (filter: 'teams' | 'levels' | 'contractTypes') => {
    setActiveSubPanel(filter);
  };

  useEffect(() => {
    const teamFilters = props.teams.contentSelected;
    const levelFilters = props.levels.contentSelected;
    const contractTypeFilters = props.contractTypes.contentSelected;
    const totalAmount =
      teamFilters.length + contractTypeFilters.length + levelFilters.length;
    setTotalAmountOfFilters(totalAmount);
  }, [
    props.contractTypes.contentSelected,
    props.teams.contentSelected,
    props.levels.contentSelected,
  ]);
  const { t } = useTranslations();

  const closeAllPanels = () => {
    closeFilterPanel();
    setActiveSubPanel('');
  };
  return (
    <>
      <MobileFilterBlock onClick={OpenMobileFilters}>
        <MobileFilterText>
          {totalAmountOfFilters > 0
            ? t('filter.filters')
            : t('filter.add_filters')}
        </MobileFilterText>
        <MobileFilterAmount>
          {totalAmountOfFilters > 0 ? (
            <TotalAmount>{totalAmountOfFilters}</TotalAmount>
          ) : (
            <Icon icon='icn_config' size='20px' color={`${colors.BlueMoon}`} />
          )}
        </MobileFilterAmount>
      </MobileFilterBlock>

      {MobileFilterOpen && (
        <Overlay>
          <BackgroundLayer onClick={closeAllPanels}>
            <CloseWrapper onClick={closeAllPanels}>
              <Icon icon='icn_cross' size='20px' color={`${colors.White}`} />
            </CloseWrapper>
          </BackgroundLayer>
          <Panel>
            <PanelInformation>
              <PanelHeader>
                <PanelHeaderTitle>{t('filter.filters')}</PanelHeaderTitle>
                <ClearFilters
                  onClick={() => {
                    clearMobileFilters();
                    closeFilterPanel();
                  }}
                >
                  {t('filter.clear_all')}
                </ClearFilters>
              </PanelHeader>
              <MobileFilters>
                <Filter onClick={() => openSubFilters('teams')}>
                  <FilterTitle>{t('jobs.team_filter_label')}</FilterTitle>
                  <FilterInformation>
                    <AtciveFilters>
                      {props.teams.contentSelected.length}
                    </AtciveFilters>
                    <Icon
                      icon='icn_arrowRight'
                      size='20px'
                      color={`${colors.BlueDark}`}
                    />
                  </FilterInformation>
                </Filter>
                <Filter onClick={() => openSubFilters('levels')}>
                  <FilterTitle>{t('jobs.level_filter_label')}</FilterTitle>
                  <FilterInformation>
                    <AtciveFilters>
                      {props.levels.contentSelected.length}
                    </AtciveFilters>
                    <Icon
                      icon='icn_arrowRight'
                      size='20px'
                      color={`${colors.BlueDark}`}
                    />
                  </FilterInformation>
                </Filter>
                <Filter onClick={() => openSubFilters('contractTypes')}>
                  <FilterTitle>{t('jobs.contract_filter_label')}</FilterTitle>
                  <FilterInformation>
                    <AtciveFilters>
                      {props.contractTypes.contentSelected.length}
                    </AtciveFilters>
                    <Icon
                      icon='icn_arrowRight'
                      size='20px'
                      color={`${colors.BlueDark}`}
                    />
                  </FilterInformation>
                </Filter>
              </MobileFilters>
            </PanelInformation>
            <SaveFilter closeFilterPanel={closeFilterPanel} />
          </Panel>
          {activeSubPanel && (
            <MobileSubPanelFilters
              category={'jobs'}
              filterName={activeSubPanel}
              filterOptions={props[activeSubPanel].content}
              onChangeHandler={props[activeSubPanel].handleFilter}
              isSelected={(value) =>
                props[activeSubPanel].contentSelected.includes(value)
              }
              closeSubPanel={() => setActiveSubPanel('')}
              clearFilter={() => clearMobileFilters(activeSubPanel)}
              closePanel={closeAllPanels}
            />
          )}
        </Overlay>
      )}
    </>
  );
};

export default MobileJobFilters;
